import { useDispatch, useSelector } from '@tabeeb/store'
import { useCallback, useMemo } from 'react'

import { getIsCurrentUserPresenter } from '@tabeeb/shared/content/selectors'
import { getSelectedUserId } from '@tabeeb/modules/recording/selectors'
import { isLocalRecordingActive } from '@tabeeb/modules/recording/services/localRecordingService'
import { openMuteCameraDialog } from '@tabeeb/modules/whiteboard/actions'
import { muteRemoteVideo, muteVideo } from '@tabeeb/modules/presentationToolbar/actions/muteVideo'

import { useHotkey } from '@collaborate-health/uikit/hooks'
import { ToggleCamera } from '@tabeeb/shared/constants/keyboardShortcuts'
import { VideoCallUser } from '@tabeeb/modules/../users/types'
import { getIsMuteVideoInProgress } from '@tabeeb/modules/presentationToolbar/selectors'

import { VIDEO_MUTED } from '../constants'
import { getCurrentUserId, getIsCallPreviewOpen, getIsUserVideoPlaying, getSelectedUser } from '../selectors'

interface UseUserCameraProps {
  user: VideoCallUser | null
  enableHotkey?: boolean
}

const useUserCamera = ({ user, enableHotkey = false }: UseUserCameraProps) => {
  const dispatch = useDispatch()

  const currentUserId = useSelector(getCurrentUserId)
  const isPresenter = useSelector(getIsCurrentUserPresenter)
  const selectedCallUser = useSelector(getSelectedUser)
  const isMuted = useSelector((state) => !getIsUserVideoPlaying(state, { Id: user?.id! }))
  const isMuteInProgress = useSelector(getIsMuteVideoInProgress)
  const selectedUserId = useSelector((state) => getSelectedUserId(state))
  const isCallPreviewOpen = useSelector(getIsCallPreviewOpen)

  const handleClick = useCallback(() => {
    if (!user || isMuteInProgress === true || (!user.isOnline && !isCallPreviewOpen)) {
      return
    }

    if (user.id === selectedUserId && !isMuted && isLocalRecordingActive()) {
      dispatch(openMuteCameraDialog())
      return
    }

    if (user.id === currentUserId) {
      dispatch(muteVideo())
      if (isCallPreviewOpen) {
        localStorage.setItem(VIDEO_MUTED, JSON.stringify(!isMuted))
      }
    } else if (isPresenter) {
      dispatch(muteRemoteVideo(user.id))
    }
  }, [currentUserId, dispatch, isCallPreviewOpen, isMuteInProgress, isMuted, isPresenter, selectedUserId, user])

  const isHotkeyEnabled = useMemo(() => {
    return (user && user.id === currentUserId && enableHotkey) || false
  }, [currentUserId, enableHotkey, user])

  useHotkey(
    ToggleCamera,
    () => {
      handleClick()
    },
    { preventDefault: true, enabled: isHotkeyEnabled }
  )

  return useMemo(
    () => ({
      isMuted,
      isPresenter,
      currentUserId,
      selectedCallUser,
      handleClick,
    }),
    [currentUserId, handleClick, isMuted, isPresenter, selectedCallUser]
  )
}

export default useUserCamera

